import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMeBootstrap } from '@/hooks/useMeBootstrap';
import { useSession } from '@/state/session';

/**
 * Wraps admin routes. State decisions are made from the bootstrap QUERY,
 * not just the Zustand session store — the store hydrates from the query
 * inside a useEffect, which runs after render. Reading purely from the
 * store would race the post-resolve render and bounce auth'd users back
 * to /login on hard refresh.
 *
 * Decision table (in order):
 *   1. Query has not yet succeeded once → render Loading (no decision yet)
 *   2. Query returned user=null → redirect to /login?next=…
 *   3. User is signed in but not isAdmin → 403 panel
 *   4. User is admin but session NOT elevated → re-enter admin password panel
 *   5. Authed + admin + elevated → render children
 */
export function AdminGuard({ children }: { children: ReactNode }) {
  const query = useMeBootstrap();
  const storeUser = useSession((s) => s.user);
  const location = useLocation();

  // Prefer the most recent successful query result; fall back to the
  // session store for the fast path on cached refetches.
  const user = query.data?.user ?? storeUser;

  // 1. Nothing decided yet — wait for the first successful response.
  if (!query.isFetched && !storeUser) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }

  // 2. Confirmed anonymous.
  if (!user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  // 3. Authed but not admin.
  if (!user.isAdmin) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center space-y-4">
        <h1 className="font-display text-cabinet-flame-red text-3xl uppercase tracking-widest">
          Admin only
        </h1>
        <p className="text-cabinet-cream/70">
          You're signed in, but this area is restricted to tournament admins.
        </p>
      </main>
    );
  }

  // 4. Admin but session not elevated.
  if (!user.sessionElevated) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center space-y-4">
        <h1 className="font-display text-cabinet-gold text-3xl uppercase tracking-widest">
          Re-enter admin password
        </h1>
        <p className="text-cabinet-cream/70">
          Admin tools require re-authentication for this session.
        </p>
        <p>
          <a
            href={`/login?next=${encodeURIComponent(location.pathname)}`}
            className="text-cabinet-gold underline"
          >
            Sign in again →
          </a>
        </p>
      </main>
    );
  }

  return <>{children}</>;
}
